import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CountdownTimer } from './CountdownTimer';
import { BundleUpsellPopup } from './BundleUpsellPopup';
import { Check, Gift, Lock, CalendarCheck, ShoppingCart, ChefHat, ChevronDown, ClipboardList, Zap, Download, ShieldCheck, Star, UtensilsCrossed, Users } from 'lucide-react';
interface ValueStackProps {
  basePrice?: number;
  popupStrikethroughPrice?: string;
  popupSpecialPrice?: string;
}
export function ValueStack({
  basePrice = 12.99,
  popupStrikethroughPrice,
  popupSpecialPrice
}: ValueStackProps) {
  const [countdownVisible, setCountdownVisible] = useState(true);
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const [bump1Checked, setBump1Checked] = useState(false);
  const [bump2Checked, setBump2Checked] = useState(false);
  const [showBundlePopup, setShowBundlePopup] = useState(false);
  // Build dynamic cart URL based on bumps
  const buildCartUrl = () => {
    if (bump1Checked && bump2Checked) return 'https://nutrinatural.online/cart/54018733769033:1';
    if (bump1Checked) return 'https://nutrinatural.online/cart/54018706997577:1';
    if (bump2Checked) return 'https://nutrinatural.online/cart/54018718237001:1';
    return 'https://nutrinatural.online/cart/54017585447241:1';
  };
  const fireInitiateCheckout = () => {
    try {
      if (typeof (window as any).fbq === 'function') {
        ;
        (window as any).fbq('track', 'InitiateCheckout');
      }
    } catch (e) {
      // silently fail
    }
  };
  const handleCtaClick = () => {
    if (!bump1Checked && !bump2Checked) {
      setShowBundlePopup(true);
    } else {
      fireInitiateCheckout();
      window.location.href = buildCartUrl();
    }
  };
  // Calculate dynamic total price
  const calculateTotal = () => {
    let total = basePrice;
    if (bump1Checked) total += 5.99;
    if (bump2Checked) total += 5.99;
    return total.toFixed(2);
  };
  // Check on mount if the 24h timer already expired
  useEffect(() => {
    const startTime = localStorage.getItem('nutripeques-countdown-start');
    if (startTime) {
      const endTime = parseInt(startTime, 10) + 24 * 60 * 60 * 1000;
      if (Date.now() >= endTime) {
        setCountdownVisible(false);
      }
    }
  }, []);
  const stackItems = [{
    name: '📘 Guía de Fundamentos',
    value: '$15 USD'
  }, {
    name: '🌞 Mañanas con Energía (16 desayunos)',
    value: '$8 USD'
  }, {
    name: '🍲 Platos que Nutren (22 comidas)',
    value: '$12 USD'
  }, {
    name: '🍎 Snacks Inteligentes (12 meriendas)',
    value: '$8 USD'
  }, {
    name: '🎉 Gustitos Conscientes (10 recetas)',
    value: '$6 USD'
  }, {
    name: '📋 Sistema NutriKids planificación semanal',
    value: '$10 USD'
  }, {
    name: '📊 Tabla de respuestas para conflictos en mesa',
    value: '$5 USD'
  }, {
    name: '💡 Tips de congelación y reutilización',
    value: '$4 USD'
  }];
  const bonusDetails = [{
    number: 1,
    icon: ChefHat,
    iconColor: 'text-[#6A1B9A]',
    iconBg: 'bg-[#F3E5F5]',
    accentColor: 'border-[#6A1B9A]',
    badgeBg: 'bg-[#6A1B9A]',
    title: '"Guía Mini-Chefs: Estrategia de Exposición"',
    value: '$18.00 USD',
    image: 'https://cdn.magicpatterns.com/uploads/a6Dgd47E2nydzg7Q35XMHZ/minichefs.png',
    intro: 'Si tu hijo rechaza todo lo nuevo, el problema no empieza en el plato. La Guía Mini-Chefs te enseña a usar micro-tareas de 3 minutos para que tu hijo se exponga a nuevos alimentos sin presión.',
    features: ['El Mapa de Progreso (mirar → tocar → oler → probar)', 'Tareas por edad (1–2 / 3–4 / 5–7 años)', 'Scripts exactos para invitar sin presión y responder al "NO"', 'Plan de exposición de 7 días + tabla de seguimiento', '12 "Misiones Mini-Chefs" listas para usar', 'Checklist imprimible']
  }, {
    number: 2,
    icon: Users,
    iconColor: 'text-[#1565C0]',
    iconBg: 'bg-[#E3F2FD]',
    accentColor: 'border-[#1565C0]',
    badgeBg: 'bg-[#1565C0]',
    title: '"La Mesa en Familia" — Comunidad Privada NutriKids',
    value: '$60.00 USD al año',
    image: 'https://cdn.magicpatterns.com/uploads/wXe3LaMMZ1A9ijbxYbyZvS/comunidadnutripeques.png',
    intro: 'El grupo privado de Facebook exclusivo para familias NutriKids. Un espacio real, sin juicios, donde madres y padres comparten avances, dudas y victorias en la mesa. No estás sola en esto.',
    features: ['Acceso inmediato después de tu compra', 'Soporte real cuando una receta no sale como esperabas', 'Inspiración de familias reales', 'Tips y recetas extra solo para el grupo', 'Celebra las victorias con quienes te entienden', 'Preguntas respondidas por la comunidad y el equipo NutriKids']
  }];
  const bonusStackItems = [{
    icon: ChefHat,
    iconColor: 'text-[#6A1B9A]',
    iconBg: 'bg-[#F3E5F5]',
    name: 'Guía Mini-Chefs: Estrategia de Exposición',
    value: '$18.00 USD',
    bounce: false
  }, {
    icon: Users,
    iconColor: 'text-[#1565C0]',
    iconBg: 'bg-[#E3F2FD]',
    name: 'La Mesa en Familia — Comunidad Privada',
    value: '$60.00 USD',
    bounce: true
  }];
  return <section id="oferta" className="py-20 px-4 bg-gradient-to-b from-[#FFF8F0] to-[#FFE0B2]/50" data-id="element-631">
      <div className="max-w-lg md:max-w-3xl mx-auto" data-id="element-632">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border-4 border-[#FF8A65] relative" data-id="element-633">
          {/* Header */}
          <div id="oferta-header" className="bg-[#FF8A65] text-white text-center py-6 px-4" data-id="element-634">
            <h2 className="text-2xl md:text-3xl font-black tracking-wide" data-id="element-635">
              TODO LO QUE NECESITAS — UN SOLO PRECIO
            </h2>
          </div>

          <div className="p-6 md:p-10" data-id="element-636">
            {/* Stack List */}
            <div className="space-y-3 mb-4" data-id="element-637">
              {stackItems.map((item, idx) => <div key={idx} className="flex justify-between items-center border-b border-gray-100 pb-3" data-id="element-638">
                  <div className="flex items-center gap-2" data-id="element-639">
                    <Check className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-640" />
                    <span className="text-gray-700 font-medium" data-id="element-641">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-gray-400 line-through text-sm shrink-0 ml-4" data-id="element-642">
                    {item.value}
                  </span>
                </div>)}
              <div className="flex justify-between items-center bg-[#FFF3E0] p-3 rounded-xl border border-[#FFE0B2]" data-id="element-643">
                <div className="flex items-center gap-2" data-id="element-644">
                  <Gift className="w-5 h-5 text-[#E65100] shrink-0" data-id="element-645" />
                  <span className="text-[#E65100] font-bold" data-id="element-646">
                    Todas las actualizaciones futuras
                  </span>
                </div>
                <span className="text-[#E65100] font-black shrink-0 ml-4" data-id="element-647">
                  GRATIS
                </span>
              </div>
            </div>

            {/* Bonus items in stack */}
            <div className="mb-8" data-id="element-648">
              <div className="flex items-center gap-2 mb-3 mt-6" data-id="element-649">
                <Gift className="w-5 h-5 text-[#E65100]" data-id="element-650" />
                <span className="text-sm font-bold text-[#E65100] uppercase tracking-wider" data-id="element-651">
                  + 2 Regalos especiales gratis hoy
                </span>
              </div>

              <div className="space-y-2" data-id="element-652">
                {bonusStackItems.map((bonus, idx) => {
                const BonusIcon = bonus.icon;
                const rowContent = <>
                      <div className="flex items-center gap-2 min-w-0" data-id="element-653">
                        <div className={`${bonus.iconBg} p-1.5 rounded-lg shrink-0`} data-id="element-654">
                          <BonusIcon className={`w-4 h-4 ${bonus.iconColor}`} data-id="element-655" />
                        </div>
                        <span className="text-gray-800 font-bold text-sm" data-id="element-656">
                          {bonus.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3" data-id="element-657">
                        <span className="text-gray-400 line-through text-xs" data-id="element-658">
                          {bonus.value}
                        </span>
                        <span className="bg-[#4CAF50] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-659">
                          GRATIS
                        </span>
                      </div>
                    </>;
                if (bonus.bounce) {
                  return <motion.div key={idx} animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} className="flex justify-between items-center bg-gradient-to-r from-[#E3F2FD] to-[#FFFDE7] p-3 rounded-xl border-2 border-[#1565C0] shadow-[0_4px_14px_rgba(21,101,192,0.2)]" data-id="element-660">
                        {rowContent}
                      </motion.div>;
                }
                return <div key={idx} className="flex justify-between items-center bg-[#FFFDE7] p-3 rounded-xl border border-[#FFF9C4]" data-id="element-661">
                      {rowContent}
                    </div>;
              })}
              </div>

              {/* Leer más toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-3 flex items-center gap-1.5 mx-auto text-[#E65100] font-bold text-sm hover:text-[#BF360C] transition-colors" data-id="element-662">
                {showBonusDetails ? 'Ver menos' : 'Ver detalles de los regalos'}
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showBonusDetails ? 'rotate-180' : ''}`} data-id="element-663" />
              </button>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-664">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4
              }} className="overflow-hidden" data-id="element-665">
                    <div className="mt-4 space-y-6" data-id="element-666">
                      {bonusDetails.map((bonus, idx) => {
                    const BonusDetailIcon = bonus.icon;
                    return <div key={idx} className={`bg-gray-50 rounded-2xl p-5 border-l-4 ${bonus.accentColor}`} data-id="element-667">
                            <div className="flex items-center gap-3 mb-3" data-id="element-668">
                              <div className={`${bonus.iconBg} p-2 rounded-xl`} data-id="element-669">
                                <BonusDetailIcon className={`w-5 h-5 ${bonus.iconColor}`} data-id="element-670" />
                              </div>
                              <div data-id="element-671">
                                <span className={`${bonus.badgeBg} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider`} data-id="element-672">
                                  Regalo #{bonus.number}
                                </span>
                                <h4 className="text-base font-black text-gray-900 mt-1" data-id="element-673">
                                  {bonus.title}
                                </h4>
                              </div>
                            </div>

                            <div className="flex justify-center mb-4" data-id="element-674">
                              <div className="relative" data-id="element-675">
                                <div className="absolute inset-0 bg-gradient-to-b from-orange-200/60 via-orange-100/40 to-transparent rounded-2xl blur-xl scale-110 -z-10" data-id="element-676" />
                                <img src={bonus.image} alt={bonus.title} className="w-full max-w-[220px] h-auto rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12),0_4px_12px_rgba(255,138,101,0.25)] transform hover:scale-[1.02] transition-transform duration-300" data-id="element-677" />
                              </div>
                            </div>

                            <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-678">
                              {bonus.intro}
                            </p>

                            <div className="space-y-2" data-id="element-679">
                              <p className="text-xs font-bold text-gray-900 flex items-center gap-1.5" data-id="element-680">
                                <ClipboardList className="w-3.5 h-3.5 text-gray-500" data-id="element-681" />
                                Incluye:
                              </p>
                              <ul className="space-y-1.5" data-id="element-682">
                                {bonus.features.map((feature, fIdx) => <li key={fIdx} className="flex items-start gap-2 text-sm" data-id="element-683">
                                    <Check className="w-4 h-4 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-684" />
                                    <span className="text-gray-700" data-id="element-685">
                                      {feature}
                                    </span>
                                  </li>)}
                              </ul>
                            </div>

                            <p className="text-center text-sm font-bold text-gray-900 mt-4" data-id="element-686">
                              Valor:{' '}
                              <span className="line-through text-gray-400" data-id="element-687">
                                {bonus.value}
                              </span>{' '}
                              <span className="text-[#4CAF50]" data-id="element-688">GRATIS hoy</span>
                            </p>
                          </div>;
                  })}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Full bundle mockup */}
            <div id="oferta-resumen" className="text-center mb-8 scroll-mt-4" data-id="element-689">
              <h3 className="text-lg md:text-xl font-black text-gray-900 leading-snug mb-1" data-id="element-690">
                Esto es todo lo que recibes hoy con tu compra de NutriKids.
              </h3>
              <p className="text-base md:text-lg font-bold text-[#E65100] mb-5" data-id="element-691">
                Todo lo que necesitas para que coma bien
              </p>
              <div className="relative flex justify-center" data-id="element-692">
                <div className="absolute inset-0 bg-gradient-to-b from-orange-200/50 via-orange-100/30 to-transparent rounded-3xl blur-2xl scale-105 -z-10" data-id="element-693" />
                <motion.img src="https://cdn.magicpatterns.com/uploads/8eT9sCTd6Vz8rHbU1qEDf3/nutrikidsgiftsmono.png" alt="NutriKids + Guía Mini-Chefs — Todo lo que recibes" className="w-full max-w-md md:max-w-lg h-auto rounded-3xl border-4 border-white shadow-[0_16px_50px_rgba(0,0,0,0.2),0_6px_20px_rgba(255,138,101,0.35),0_0_0_1px_rgba(0,0,0,0.05)]" animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-694" />
              </div>
            </div>

            {/* Top 3 Testimonials */}
            <div className="mb-8" data-id="element-695">
              <div className="text-center mb-5" data-id="element-696">
                <div className="inline-flex items-center gap-2 bg-[#FFF3E0] px-4 py-1.5 rounded-full border border-[#FFE0B2] mb-3" data-id="element-697">
                  <Star className="w-4 h-4 fill-[#FFB300] text-[#FFB300]" data-id="element-698" />
                  <span className="text-[#E65100] font-bold text-xs uppercase tracking-wider" data-id="element-699">
                    Lo que dicen las mamás
                  </span>
                </div>
                <h4 className="text-lg md:text-xl font-black text-gray-900 leading-snug" data-id="element-700">
                  3 historias de mamás con las mismas preguntas que tú, que
                  agradecen formar parte de la comunidad
                </h4>
              </div>

              <div className="space-y-3" data-id="element-701">
                {[{
                quote: 'Le mostré una receta a mi pediatra y me preguntó dónde la había conseguido. Le conté y me dijo que siguiera así porque los números de hierro de mi hija habían mejorado. Eso me lo dice todo.',
                name: 'Rocío',
                desc: 'Mamá de Emilia, 2 años — Florida, USA'
              }, {
                quote: 'Hubo un momento en que me senté y lloré porque no sabía qué más hacer. Sentía que fallaba como mamá porque mi hijo no comía y yo me quedaba sin ideas. NutriKids me devolvió la confianza en la mesa.',
                name: 'Natalia',
                desc: 'Mamá de Thiago, 3 años — California, USA'
              }, {
                quote: 'Mi hija solo quería galletitas, jugos, todo de paquete. Yo ya había resignado. Pensé que era así y listo. Hoy come cosas que antes ni miraba. Sin pelea. Sin llanto. Sin que yo tenga que rogar.',
                name: 'Tatiana',
                desc: 'Mamá de Sofía, 3 años — New York, USA'
              }].map((t, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: idx * 0.1
              }} className="bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-[#FFE0B2]" data-id="element-702">
                    <div className="flex gap-1 mb-2" data-id="element-703">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#FFB300] text-[#FFB300]" data-id="element-704" />)}
                    </div>
                    <p className="text-gray-700 text-sm md:text-base italic mb-3 leading-relaxed" data-id="element-705">
                      "{t.quote}"
                    </p>
                    <div data-id="element-706">
                      <div className="font-bold text-gray-900 text-sm" data-id="element-707">
                        {t.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-708">{t.desc}</div>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Pricing Area */}
            <div className="bg-[#FDFBF7] rounded-2xl p-6 md:p-8 text-center border border-gray-200 mb-8 relative overflow-hidden" data-id="element-709">
              <div className="absolute top-0 right-0 bg-[#E65100] text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-710">
                67% DE DESCUENTO
              </div>

              <div className="mb-2" data-id="element-711">
                <span className="text-gray-500 font-medium" data-id="element-712">Valor total: </span>
                <span className="text-gray-400 line-through text-lg" data-id="element-713">
                  $93 USD
                </span>
              </div>

              <div className="mb-4" data-id="element-714">
                <span className="text-gray-600 font-bold" data-id="element-715">
                  Precio regular:{' '}
                </span>
                <span className="text-gray-500 line-through text-xl" data-id="element-716">
                  $39 USD
                </span>
              </div>

              <div className="text-center mb-2" data-id="element-717">
                <span className="block text-sm font-bold text-[#4CAF50] uppercase tracking-wider mb-1" data-id="element-718">
                  Oportunidad Única de Lanzamiento
                </span>
                <div className="flex justify-center items-start gap-1" data-id="element-719">
                  <span className="text-3xl font-bold text-[#2E7D32] mt-2" data-id="element-720">
                    $
                  </span>
                  <span className="text-7xl md:text-8xl font-black text-[#2E7D32] leading-none transition-all duration-300" data-id="element-721">
                    {calculateTotal()}
                  </span>
                  <span className="text-xl font-bold text-[#2E7D32] mt-2" data-id="element-722">
                    USD
                  </span>
                </div>
              </div>
            </div>

            {/* Urgency — 24h countdown only when not expired */}
            {countdownVisible && <div className="text-center mb-8" data-id="element-723">
                <p className="text-[#D84315] font-bold mb-2" data-id="element-724">
                  ⏰ Esta oferta desaparece en:
                </p>
                <CountdownTimer hours={24} storageKey="nutripeques-countdown-start" onExpire={() => setCountdownVisible(false)} data-id="element-725" />
                <p className="text-gray-500 text-sm mt-2" data-id="element-726">
                  Después el precio sube a $39 USD
                </p>
              </div>}

            {/* CTA and Order Bumps */}
            <div className="flex flex-col items-center" data-id="element-727">
              <button type="button" onClick={handleCtaClick} className="w-full min-h-[72px] bg-[#4CAF50] hover:bg-[#388E3C] text-white text-xl md:text-2xl font-black rounded-2xl shadow-[0_8px_0_#2E7D32] hover:shadow-[0_4px_0_#2E7D32] hover:translate-y-1 transition-all flex items-center justify-center text-center px-4 py-4 mb-4 cursor-pointer" data-id="element-728">
                → QUIERO EMPEZAR A NUTRIR CON CALMA
              </button>

              {/* Trust Badges — after first CTA */}
              <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-id="element-729">
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-4 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-730">
                  <Zap className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-731" />
                  <span className="text-gray-800 font-bold text-sm md:text-base" data-id="element-732">
                    Acceso inmediato
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-4 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-733">
                  <Download className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-734" />
                  <span className="text-gray-800 font-bold text-sm md:text-base" data-id="element-735">
                    Descarga directa
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl py-3.5 px-4 shadow-[0_4px_12px_rgba(76,175,80,0.15)]" data-id="element-736">
                  <ShieldCheck className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-737" />
                  <span className="text-gray-800 font-bold text-sm md:text-base" data-id="element-738">
                    Garantía de 15 días
                  </span>
                </div>
              </div>

              {/* ORDER BUMPS */}
              <div className="w-full mt-6 space-y-8" data-id="element-739">
                {/* Order Bump 1: Alfajores Saludables */}
                <div className="relative pt-3" data-id="element-740">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 bg-[#FF6F00] text-white text-[11px] md:text-xs font-black px-3 py-1 rounded-full shadow-md flex items-center gap-1 whitespace-nowrap" data-id="element-741">
                    <Star className="w-3.5 h-3.5 fill-current" data-id="element-742" /> ALTAMENTE
                    RECOMENDADO
                  </div>
                  <div onClick={() => setBump1Checked(!bump1Checked)} className={`relative cursor-pointer rounded-2xl border-[3px] border-solid transition-all duration-300 overflow-hidden ${bump1Checked ? 'border-[#4CAF50] bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] shadow-lg shadow-green-100' : 'border-[#FFB74D] bg-gradient-to-br from-[#FFF8E1] to-[#FFF3E0] hover:border-[#FF9800] hover:shadow-md'}`} data-id="element-743">
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_3s_infinite] pointer-events-none" data-id="element-744" />

                    {/* Top ribbon */}
                    <div className="bg-[#FF8A65] text-white text-center py-2 px-4 font-black text-sm tracking-wide relative z-10" data-id="element-745">
                      🍪 OFERTA EXCLUSIVA — SÚMALO A TU PEDIDO
                    </div>

                    <div className="p-4 md:p-5 relative z-10" data-id="element-746">
                      <div className="flex items-start gap-4" data-id="element-747">
                        {/* Checkbox */}
                        <div className={`mt-1 w-7 h-7 rounded-lg border-[3px] flex items-center justify-center shrink-0 transition-all duration-200 ${bump1Checked ? 'bg-[#4CAF50] border-[#4CAF50]' : 'border-gray-400 bg-white'}`} data-id="element-748">
                          {bump1Checked && <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-749" />}
                        </div>

                        <div className="flex-1 min-w-0" data-id="element-750">
                          <p className="font-black text-gray-900 text-base md:text-lg leading-tight mb-2" data-id="element-751">
                            ¡SÍ! SUMAR ALFAJORES, COOKIES Y BOMBONES 🍫
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3" data-id="element-752">
                            <div className="relative shrink-0" data-id="element-753">
                              <div className="absolute inset-0 bg-gradient-to-br from-orange-200/50 to-amber-100/40 rounded-xl blur-lg scale-110 -z-10" data-id="element-754" />
                              <img src="https://cdn.magicpatterns.com/uploads/6EwvWEpMVmnj3sdEJ44A7P/order_bump_1_recetario_alfajores_saludables.png" alt="Recetario de Alfajores, Cookies y Bombones Saludables" className="w-24 h-24 object-cover rounded-xl shadow-[0_6px_20px_rgba(0,0,0,0.1),0_3px_8px_rgba(255,138,101,0.3)] bg-white" data-id="element-755" />
                            </div>
                            <div data-id="element-756">
                              <p className="text-gray-700 text-sm leading-relaxed" data-id="element-757">
                                Evita los ultraprocesados del supermercado.
                                Llévate{' '}
                                <strong data-id="element-758">
                                  15 recetas de alfajores, cookies y bombones
                                  saludables
                                </strong>{' '}
                                (sin azúcar refinada) para que tu peque disfrute
                                de un "permitido" que realmente lo nutre.
                                Ideales para la lonchera o meriendas rápidas.
                              </p>
                              <div className="mt-3 flex items-center gap-2" data-id="element-759">
                                <span className="text-gray-500 line-through text-sm font-medium" data-id="element-760">
                                  $12.99 USD
                                </span>
                                <span className="bg-[#E65100] text-white font-black text-sm px-3 py-1 rounded-full shadow-sm" data-id="element-761">
                                  Solo $5.99 USD
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Bump 2: Cumpleaños Saludables */}
                <div className="relative pt-3" data-id="element-762">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 bg-[#FF6F00] text-white text-[11px] md:text-xs font-black px-3 py-1 rounded-full shadow-md flex items-center gap-1 whitespace-nowrap" data-id="element-763">
                    <Star className="w-3.5 h-3.5 fill-current" data-id="element-764" /> ALTAMENTE
                    RECOMENDADO
                  </div>
                  <div onClick={() => setBump2Checked(!bump2Checked)} className={`relative cursor-pointer rounded-2xl border-[3px] border-solid transition-all duration-300 overflow-hidden ${bump2Checked ? 'border-[#4CAF50] bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] shadow-lg shadow-green-100' : 'border-[#CE93D8] bg-gradient-to-br from-[#F3E5F5] to-[#FCE4EC] hover:border-[#AB47BC] hover:shadow-md'}`} data-id="element-765">
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_3s_infinite] pointer-events-none" data-id="element-766" />

                    {/* Top ribbon */}
                    <div className="bg-[#7B1FA2] text-white text-center py-2 px-4 font-black text-sm tracking-wide relative z-10" data-id="element-767">
                      🎈 OFERTA ÚNICA — NO VOLVERÁ A APARECER
                    </div>

                    <div className="p-4 md:p-5 relative z-10" data-id="element-768">
                      <div className="flex items-start gap-4" data-id="element-769">
                        {/* Checkbox */}
                        <div className={`mt-1 w-7 h-7 rounded-lg border-[3px] flex items-center justify-center shrink-0 transition-all duration-200 ${bump2Checked ? 'bg-[#4CAF50] border-[#4CAF50]' : 'border-gray-400 bg-white'}`} data-id="element-770">
                          {bump2Checked && <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-771" />}
                        </div>

                        <div className="flex-1 min-w-0" data-id="element-772">
                          <p className="font-black text-gray-900 text-base md:text-lg leading-tight mb-2" data-id="element-773">
                            🚀 CUMPLEAÑOS SALUDABLES: EL SISTEMA PARA FIESTAS
                            ÉPICAS
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3" data-id="element-774">
                            <div className="relative shrink-0" data-id="element-775">
                              <div className="absolute inset-0 bg-gradient-to-br from-purple-200/50 to-pink-100/40 rounded-xl blur-lg scale-110 -z-10" data-id="element-776" />
                              <img src="https://cdn.magicpatterns.com/uploads/3mBhgGstWx4t5wPxfbNT7R/order_bump_2_libro_cumpleanos_saludable.png" alt="Cumpleaños Saludables: El Sistema para Fiestas Épicas" className="w-24 h-24 object-cover rounded-xl shadow-[0_6px_20px_rgba(0,0,0,0.1),0_3px_8px_rgba(123,31,162,0.25)] bg-white" data-id="element-777" />
                            </div>
                            <div data-id="element-778">
                              <p className="text-gray-700 text-sm leading-relaxed" data-id="element-779">
                                No dejes que los ultraprocesados arruinen su
                                salud en las fiestas. No son solo recetas — es
                                un <strong data-id="element-780">sistema completo</strong> con{' '}
                                <strong data-id="element-781">20 recetas</strong> de snacks, tortas y
                                bebidas saludables, más{' '}
                                <strong data-id="element-782">
                                  juegos diseñados para que los niños amen la
                                  comida real
                                </strong>{' '}
                                y se la pidan a sus papás después. ¡Paz mental
                                en cada festejo!
                              </p>
                              <div className="mt-3 flex items-center gap-2" data-id="element-783">
                                <span className="text-gray-500 line-through text-sm font-medium" data-id="element-784">
                                  $15.99 USD
                                </span>
                                <span className="bg-[#7B1FA2] text-white font-black text-sm px-3 py-1 rounded-full shadow-sm" data-id="element-785">
                                  Solo $5.99 USD
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 mb-4" data-id="element-786">
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-4 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-787">
                  <Zap className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-788" />
                  <span className="text-gray-800 font-bold text-sm md:text-base" data-id="element-789">
                    Acceso inmediato
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-4 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-790">
                  <Download className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-791" />
                  <span className="text-gray-800 font-bold text-sm md:text-base" data-id="element-792">
                    Descarga directa
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl py-3.5 px-4 shadow-[0_4px_12px_rgba(76,175,80,0.15)]" data-id="element-793">
                  <ShieldCheck className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-794" />
                  <span className="text-gray-800 font-bold text-sm md:text-base" data-id="element-795">
                    Garantía de 15 días
                  </span>
                </div>
              </div>

              {/* Summary Stack */}
              <div className="w-full bg-[#FAFAFA] rounded-2xl p-5 md:p-6 border border-gray-200 mt-4 mb-6 shadow-sm" data-id="element-796">
                <h4 className="text-center font-black text-gray-800 mb-4 text-sm tracking-widest uppercase" data-id="element-797">
                  RESUMEN DE TU PEDIDO
                </h4>
                <div className="w-12 h-1 bg-gray-200 mx-auto mb-5 rounded-full" data-id="element-798"></div>

                <div className="space-y-3 mb-5" data-id="element-799">
                  <div className="flex justify-between items-center text-sm md:text-base" data-id="element-800">
                    <span className="font-bold text-gray-800 flex items-center gap-2" data-id="element-801">
                      <span className="text-lg" data-id="element-802">📘</span> NutriKids Completo
                    </span>
                    <div className="flex items-center gap-2" data-id="element-803">
                      <span className="text-gray-400 line-through text-xs md:text-sm" data-id="element-804">
                        $39.00 USD
                      </span>
                      <span className="font-black text-[#2E7D32]" data-id="element-805">
                        ${basePrice.toFixed(2)} USD
                      </span>
                    </div>
                  </div>

                  {bump1Checked && <div className="flex justify-between items-center text-sm md:text-base animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-806">
                      <span className="font-bold text-gray-800 flex items-center gap-2" data-id="element-807">
                        <span className="text-lg" data-id="element-808">🍪</span> Alfajores, Cookies y
                        Bombones
                      </span>
                      <div className="flex items-center gap-2" data-id="element-809">
                        <span className="text-gray-400 line-through text-xs md:text-sm" data-id="element-810">
                          $12.99 USD
                        </span>
                        <span className="font-black text-[#2E7D32]" data-id="element-811">
                          $5.99 USD
                        </span>
                      </div>
                    </div>}

                  {bump2Checked && <div className="flex justify-between items-center text-sm md:text-base animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-812">
                      <span className="font-bold text-gray-800 flex items-center gap-2" data-id="element-813">
                        <span className="text-lg" data-id="element-814">🎈</span> Cumpleaños
                        Saludables
                      </span>
                      <div className="flex items-center gap-2" data-id="element-815">
                        <span className="text-gray-400 line-through text-xs md:text-sm" data-id="element-816">
                          $15.99 USD
                        </span>
                        <span className="font-black text-[#2E7D32]" data-id="element-817">
                          $5.99 USD
                        </span>
                      </div>
                    </div>}
                </div>

                <div className="border-t border-gray-200 pt-4 flex justify-between items-center" data-id="element-818">
                  <span className="font-black text-gray-900 text-lg" data-id="element-819">
                    TOTAL HOY:
                  </span>
                  <span className="font-black text-[#2E7D32] text-2xl" data-id="element-820">
                    ${calculateTotal()} USD
                  </span>
                </div>
              </div>

              {/* Second CTA Button */}
              <button type="button" onClick={handleCtaClick} className="w-full min-h-[72px] bg-[#4CAF50] hover:bg-[#388E3C] text-white text-xl md:text-2xl font-black rounded-2xl shadow-[0_8px_0_#2E7D32] hover:shadow-[0_4px_0_#2E7D32] hover:translate-y-1 transition-all flex items-center justify-center text-center px-4 py-4 cursor-pointer" data-id="element-821">
                → COMPLETAR MI PEDIDO AHORA
              </button>
            </div>
          </div>
        </motion.div>

        <p className="text-center text-gray-600 italic mt-8 text-lg" data-id="element-822">
          "No necesitas hacerlo perfecto. Necesitas empezar."
        </p>
      </div>

      <BundleUpsellPopup isOpen={showBundlePopup} onClose={() => setShowBundlePopup(false)} {...popupStrikethroughPrice ? {
      strikethroughPrice: popupStrikethroughPrice
    } : {}} {...popupSpecialPrice ? {
      specialPrice: popupSpecialPrice
    } : {}} data-id="element-823" />
    </section>;
}